import { useRef, type ReactElement, type ReactNode } from "react";

import { Button } from "./Button";
import { Modal } from "./Modal";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  children: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  /** `destructive` for actions that can't be undone. */
  tone?: "default" | "destructive";
  loading?: boolean;
  /** "cancel" focuses Abbrechen first, for confirms where a stray Enter mustn't act
   * (e.g. showing clear data while sharing a screen). Default: the dialog's first control. */
  initialFocus?: "cancel";
}

/** Asks before an action with real consequences. Abbrechen closes without
 * acting; only the confirm button runs `onConfirm`. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  children,
  confirmLabel,
  onConfirm,
  tone = "default",
  loading = false,
  initialFocus,
}: ConfirmDialogProps): ReactElement {
  const cancelRef = useRef<HTMLButtonElement>(null);
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="sm"
      {...(initialFocus === "cancel" && { initialFocusRef: cancelRef })}
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            variant={tone === "destructive" ? "destructive" : "primary"}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-text-primary">{children}</div>
    </Modal>
  );
}
